import React, { useEffect, useState } from 'react';
import { MoveUp, MoveUpRight, MoveRight, MoveDownRight, MoveDown, MoveDownLeft, MoveLeft, MoveUpLeft, Circle } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

// Embedded sedan image
const SEDAN_IMAGE = `${process.env.PUBLIC_URL || ''}/sedan-top.png`;

const ZONES = [
  { id: 'front', label: 'Front', angle: 0, Icon: MoveDown },
  { id: 'front-right', label: 'Front right', angle: 45, Icon: MoveDownLeft },
  { id: 'right', label: 'Right side', angle: 90, Icon: MoveLeft },
  { id: 'rear-right', label: 'Rear right', angle: 135, Icon: MoveUpLeft },
  { id: 'rear', label: 'Rear', angle: 180, Icon: MoveUp },
  { id: 'rear-left', label: 'Rear left', angle: 225, Icon: MoveUpRight },
  { id: 'left', label: 'Left side', angle: 270, Icon: MoveRight },
  { id: 'front-left', label: 'Front left', angle: 315, Icon: MoveDownRight },
  { id: 'roof', label: 'Roof', angle: null, Icon: Circle },
  { id: 'underside', label: 'Underside', angle: null, Icon: null },
  { id: 'other', label: 'Other', angle: null, Icon: null }
];

const ARROW_POSITIONS = {
  front: { top: '20%', left: '50%' },
  'front-right': { top: '26%', left: '74%' },
  right: { top: '50%', left: '80%' },
  'rear-right': { top: '74%', left: '74%' },
  rear: { top: '80%', left: '50%' },
  'rear-left': { top: '74%', left: '26%' },
  left: { top: '50%', left: '20%' },
  'front-left': { top: '26%', left: '26%' },
  roof: { top: '50%', left: '50%' }
};

const CLICKABLE_AREAS = {
  front: { top: '15%', left: '35%', width: '30%', height: '20%' },
  'front-right': { top: '20%', left: '60%', width: '25%', height: '25%' },
  right: { top: '40%', left: '65%', width: '25%', height: '20%' },
  'rear-right': { top: '60%', left: '60%', width: '25%', height: '25%' },
  rear: { top: '65%', left: '35%', width: '30%', height: '20%' },
  'rear-left': { top: '60%', left: '15%', width: '25%', height: '25%' },
  left: { top: '40%', left: '10%', width: '25%', height: '20%' },
  'front-left': { top: '20%', left: '15%', width: '25%', height: '25%' },
  roof: { top: '40%', left: '40%', width: '20%', height: '20%' }
};

const baseArrowClass = 'transition-all duration-200';
const activeArrowClass = 'text-sky-500 opacity-100';
const idleArrowClass = 'text-slate-400 opacity-0 group-hover/selector:opacity-30';

const buildWedgeStyle = (angle, intensity, zoneId) => {
  if (!intensity) {
    return { opacity: 0 };
  }

  // Use tighter gradient mask for different zone types
  const isLeftOrRight = zoneId === 'left' || zoneId === 'right';
  const isCorner = zoneId.includes('-');
  
  let maskGradient;
  if (isLeftOrRight) {
    maskGradient = 'radial-gradient(circle at 50% 50%, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0) 60%)';
  } else if (isCorner) {
    maskGradient = 'radial-gradient(circle at 50% 50%, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0) 70%)';
  } else {
    maskGradient = 'radial-gradient(circle at 50% 50%, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0) 80%)';
  }

  const start = angle - 22.5;
  const colour = `rgba(56, 189, 248, ${intensity})`;

  return {
    opacity: 1,
    background: `conic-gradient(from ${start}deg at 50% 50%, ${colour} 0deg 45deg, rgba(56, 189, 248, 0) 48deg 360deg)`,
    filter: `blur(${intensity > 0.5 ? 18 : 12}px)`,
    maskImage: maskGradient
  };
};

const buildRoofGlowStyle = (intensity) => {
  if (!intensity) {
    return { opacity: 0 };
  }

  return {
    opacity: 1,
    background: `radial-gradient(circle at 50% 50%, rgba(56, 189, 248, ${intensity}) 0%, rgba(56, 189, 248, 0) 40%)`,
    filter: `blur(${intensity > 0.5 ? 16 : 10}px)`
  };
};

const VehicleSurface = () => (
  <div className="absolute inset-[8%] overflow-hidden bg-transparent flex items-center justify-center">
    <img
      src={SEDAN_IMAGE}
      alt=""
      className="pointer-events-none object-contain"
      style={{ width: '70%', height: '70%' }}
    />
  </div>
);



const DamageHotspotSelector = ({
  selectedZones = [],
  onChange,
  onRemoveZone,
  checkZoneHasParts,
  filterZone = null,
  onFilterChange,
}) => {
  const [activeZones, setActiveZones] = useState(() => new Set(selectedZones));
  const [hoveredZone, setHoveredZone] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    setActiveZones(new Set(selectedZones));
  }, [selectedZones]);

  const handleZoneClick = (zoneId) => {
    const isActive = activeZones.has(zoneId);
    if (isActive) {
      // Zone already has damage — toggle the filter
      if (filterZone === zoneId) {
        onFilterChange?.(null); // Clear filter (show all)
      } else {
        onFilterChange?.(zoneId); // Set filter to this zone
      }
    } else {
      // Zone doesn't have damage — add it and set filter
      const next = new Set(activeZones);
      next.add(zoneId);
      setActiveZones(next);
      onChange?.(Array.from(next));
      onFilterChange?.(zoneId);
    }
  };

  const toggleZone = (zoneId) => {
    const next = new Set(activeZones);
    if (next.has(zoneId)) {
      const hasParts = typeof checkZoneHasParts === 'function' ? checkZoneHasParts(zoneId) : false;
      if (hasParts) {
        setConfirmDelete(zoneId);
      } else {
        next.delete(zoneId);
        setActiveZones(next);
        onChange?.(Array.from(next));
        onRemoveZone?.(zoneId);
        if (filterZone === zoneId) onFilterChange?.(null);
      }
    } else {
      next.add(zoneId);
      setActiveZones(next);
      onChange?.(Array.from(next));
    }
  };

  const confirmRemoveZone = () => {
    if (confirmDelete) {
      const next = new Set(activeZones);
      next.delete(confirmDelete);
      setActiveZones(next);
      onChange?.(Array.from(next));
      onRemoveZone?.(confirmDelete);
      if (filterZone === confirmDelete) onFilterChange?.(null);
      setConfirmDelete(null);
    }
  };

  return (
    <>
      <div className="group/selector">
        <div className="relative isolate w-full aspect-[2/3] overflow-visible">
          <div className="pointer-events-none absolute inset-[8%]">
            {ZONES.filter(z => z.Icon).map((zone) => {
              const isActive = activeZones.has(zone.id);
              const isHovered = hoveredZone === zone.id && !isActive;
              const intensity = isActive ? 0.7 : isHovered ? 0.35 : 0;
              
              if (zone.id === 'roof') {
                return (
                  <div
                    key={`wedge-${zone.id}`}
                    className="absolute inset-0 transition-opacity duration-200 ease-out"
                    style={buildRoofGlowStyle(intensity)}
                  />
                );
              }
              
              return (
                <div
                  key={`wedge-${zone.id}`}
                  className="absolute inset-0 transition-opacity duration-200 ease-out"
                  style={buildWedgeStyle(zone.angle, intensity, zone.id)}
                />
              );
            })}
          </div>

          <VehicleSurface />

          {ZONES.filter(z => z.Icon).map((zone) => {
            const isActive = activeZones.has(zone.id);
            const isFiltered = filterZone === zone.id;
            const clickableArea = CLICKABLE_AREAS[zone.id];
            const arrowPosition = ARROW_POSITIONS[zone.id];
            const Icon = zone.Icon;
            return (
              <React.Fragment key={zone.id}>
                {/* Large clickable area */}
                <button
                  type="button"
                  aria-pressed={isActive}
                  aria-label={zone.label}
                  onClick={() => handleZoneClick(zone.id)}
                  onMouseEnter={() => setHoveredZone(zone.id)}
                  onMouseLeave={() => setHoveredZone(null)}
                  className="absolute cursor-pointer transition-opacity duration-200"
                  style={{
                    top: clickableArea.top,
                    left: clickableArea.left,
                    width: clickableArea.width,
                    height: clickableArea.height,
                  }}
                />
                {/* Arrow indicator — visible for filtered zone, highlighted on hover, ghost on parent hover */}
                <div
                  className={`${baseArrowClass} ${
                    isFiltered
                      ? activeArrowClass
                      : hoveredZone === zone.id
                        ? 'text-sky-500 opacity-100'
                        : idleArrowClass
                  } pointer-events-none`}
                  style={{
                    top: arrowPosition.top,
                    left: arrowPosition.left,
                    transform: 'translate(-50%, -50%)',
                    position: 'absolute',
                  }}
                >
                  <Icon size={24} strokeWidth={2.5} />
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Additional zone buttons — stacked vertically */}
        <div className="mt-3 flex flex-col gap-1.5">
          {ZONES.filter(z => !z.Icon).map((zone) => {
            const isActive = activeZones.has(zone.id);
            const isFiltered = filterZone === zone.id;
            return (
              <Button
                key={zone.id}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => isActive ? handleZoneClick(zone.id) : toggleZone(zone.id)}
                className={`w-full transition-colors text-xs ${
                  isFiltered
                    ? 'bg-sky-100 border-sky-500 text-sky-700 hover:bg-sky-200'
                    : isActive 
                      ? 'bg-sky-50 border-sky-500 text-sky-600 hover:bg-sky-100' 
                      : 'text-slate-600 hover:text-sky-600 hover:border-sky-400'
                }`}
              >
                {zone.label}
              </Button>
            );
          })}
          {/* All button */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onFilterChange?.(null)}
            className={`w-full transition-colors text-xs ${
              filterZone === null
                ? 'bg-sky-100 border-sky-500 text-sky-700 hover:bg-sky-200 font-medium'
                : 'text-slate-600 hover:text-sky-600 hover:border-sky-400'
            }`}
          >
            All
          </Button>
        </div>
      </div>

      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Section</DialogTitle>
            <DialogDescription>
              This may delete parts and images associated with this section. Are you sure you want to remove it?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmRemoveZone}>
              Remove Section
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DamageHotspotSelector;
